import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DatePickerCalendar,
  InputCompound,
  Popover,
  Typography,
  XStack,
} from '@usepepper/sprout-kit-ui';
import { formatLocalePrice } from '@/domain/pricing/utils/pricing-utils';
import { Pressable, View } from 'react-native';
import {
  ProspectEditFormErrors,
  ProspectEditFormValues,
} from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';

const INPUT_SIZE = '$44';

const formatCloseDate = (date: Date): string =>
  new Intl.DateTimeFormat(i18n.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);

export interface ProspectEditFormProps {
  currencySymbol: string;
  errors: ProspectEditFormErrors;
  onFieldBlur: (field: keyof ProspectEditFormValues) => void;
  onFieldChange: <K extends keyof ProspectEditFormValues>(
    field: K,
    value: ProspectEditFormValues[K],
  ) => void;
  onPhoneChange: (text: string) => void;
  onWeeklyValueChange: (text: string) => void;
  values: ProspectEditFormValues;
}

export const ProspectEditForm: React.FC<ProspectEditFormProps> = ({
  currencySymbol,
  errors,
  onFieldBlur,
  onFieldChange,
  onPhoneChange,
  onWeeklyValueChange,
  values,
}) => {
  const [styles] = useStyles();

  const [isCloseDatePickerOpen, setIsCloseDatePickerOpen] = useState(false);

  return (
    <View style={styles.form}>
      <InputCompound size={INPUT_SIZE}>
        <InputCompound.Label htmlFor="prospectCompetitors">
          {i18n.t('sales_portal.edit_prospect.labels.competitors')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <InputCompound.Area
              accessibilityLabel={i18n.t(
                'sales_portal.edit_prospect.labels.competitors',
              )}
              autoCapitalize="words"
              autoCorrect={false}
              id="prospectCompetitors"
              onChangeText={(text) => onFieldChange('competitors', text)}
              placeholder={i18n.t(
                'sales_portal.edit_prospect.placeholders.competitors',
              )}
              value={values.competitors}
            />
          </InputCompound.Section>
        </InputCompound.Box>
      </InputCompound>
      <View style={styles.contactSection}>
        <InputCompound size={INPUT_SIZE}>
          <InputCompound.Label htmlFor="prospectContactName">
            {i18n.t('sales_portal.edit_prospect.labels.contact_name')}
          </InputCompound.Label>
          <InputCompound.Box>
            <InputCompound.Section>
              <InputCompound.Area
                accessibilityLabel={i18n.t(
                  'sales_portal.edit_prospect.labels.contact_name',
                )}
                autoCapitalize="words"
                autoComplete="name"
                autoCorrect={false}
                id="prospectContactName"
                onChangeText={(text) => onFieldChange('contactName', text)}
                placeholder={i18n.t(
                  'sales_portal.edit_prospect.placeholders.contact_name',
                )}
                textContentType="name"
                value={values.contactName}
              />
            </InputCompound.Section>
          </InputCompound.Box>
        </InputCompound>
        <InputCompound size={INPUT_SIZE}>
          <InputCompound.Box>
            <InputCompound.Section>
              <InputCompound.Area
                accessibilityLabel={i18n.t(
                  'sales_portal.edit_prospect.labels.contact_role',
                )}
                autoCapitalize="words"
                autoCorrect={false}
                onChangeText={(text) => onFieldChange('contactRole', text)}
                placeholder={i18n.t(
                  'sales_portal.edit_prospect.placeholders.contact_role',
                )}
                value={values.contactRole}
              />
            </InputCompound.Section>
          </InputCompound.Box>
        </InputCompound>
        <View style={styles.contactRow}>
          <View style={styles.contactRowField}>
            <InputCompound error={!!errors.contactEmail} size={INPUT_SIZE}>
              <InputCompound.Box>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={i18n.t(
                      'sales_portal.edit_prospect.labels.contact_email',
                    )}
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    keyboardType="email-address"
                    onBlur={() => onFieldBlur('contactEmail')}
                    onChangeText={(text) => onFieldChange('contactEmail', text)}
                    placeholder={i18n.t(
                      'sales_portal.edit_prospect.placeholders.contact_email',
                    )}
                    textContentType="emailAddress"
                    value={values.contactEmail}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
              {errors.contactEmail ? (
                <InputCompound.Info>{errors.contactEmail}</InputCompound.Info>
              ) : null}
            </InputCompound>
          </View>
          <View style={styles.contactRowField}>
            <InputCompound error={!!errors.contactPhone} size={INPUT_SIZE}>
              <InputCompound.Box>
                <InputCompound.Section>
                  <InputCompound.Area
                    accessibilityLabel={i18n.t(
                      'sales_portal.edit_prospect.labels.contact_phone',
                    )}
                    autoComplete="tel"
                    keyboardType="phone-pad"
                    onBlur={() => onFieldBlur('contactPhone')}
                    onChangeText={onPhoneChange}
                    placeholder={i18n.t(
                      'sales_portal.edit_prospect.placeholders.contact_phone',
                    )}
                    textContentType="telephoneNumber"
                    value={values.contactPhone}
                  />
                </InputCompound.Section>
              </InputCompound.Box>
              {errors.contactPhone ? (
                <InputCompound.Info>{errors.contactPhone}</InputCompound.Info>
              ) : null}
            </InputCompound>
          </View>
        </View>
      </View>
      <InputCompound size={INPUT_SIZE}>
        <InputCompound.Label htmlFor="prospectCloseDate">
          {i18n.t('sales_portal.edit_prospect.labels.close_date')}
        </InputCompound.Label>
        <Popover
          allowFlip
          onOpenChange={setIsCloseDatePickerOpen}
          open={isCloseDatePickerOpen}
          placement="bottom-start"
        >
          <Popover.Anchor asChild>
            <Pressable
              accessibilityLabel={i18n.t(
                'sales_portal.edit_prospect.labels.close_date',
              )}
              accessibilityRole="button"
              accessibilityState={{ expanded: isCloseDatePickerOpen }}
              id="prospectCloseDate"
              onPress={() => setIsCloseDatePickerOpen((prev) => !prev)}
            >
              <InputCompound.Box>
                <InputCompound.Section>
                  <XStack
                    alignItems="center"
                    flex={1}
                    height="100%"
                    paddingHorizontal={12}
                  >
                    <Typography
                      muted={!values.closeDate}
                      size="$sm"
                      variant="body"
                    >
                      {values.closeDate
                        ? formatCloseDate(values.closeDate)
                        : i18n.t('sales_portal.edit_prospect.select_date')}
                    </Typography>
                  </XStack>
                </InputCompound.Section>
              </InputCompound.Box>
            </Pressable>
          </Popover.Anchor>
          <Popover.Content padding="$16">
            <DatePickerCalendar
              mode="single"
              onChange={(dates: Date[]) => {
                onFieldChange('closeDate', dates[0] ?? null);
                setIsCloseDatePickerOpen(false);
              }}
              onClear={
                values.closeDate
                  ? () => {
                      onFieldChange('closeDate', null);
                      setIsCloseDatePickerOpen(false);
                    }
                  : undefined
              }
              value={values.closeDate ? [values.closeDate] : []}
            />
          </Popover.Content>
        </Popover>
      </InputCompound>
      <InputCompound error={!!errors.weeklyValue} size={INPUT_SIZE}>
        <InputCompound.Label htmlFor="prospectWeeklyValue">
          {i18n.t('sales_portal.edit_prospect.labels.weekly_value')}
        </InputCompound.Label>
        <InputCompound.Box>
          <InputCompound.Section>
            <XStack alignItems="center" flex={1} height="100%">
              <Typography muted paddingLeft={12} size="$sm" variant="body">
                {currencySymbol}
              </Typography>
              <InputCompound.Area
                accessibilityLabel={i18n.t(
                  'sales_portal.edit_prospect.labels.weekly_value',
                )}
                flex={1}
                id="prospectWeeklyValue"
                inputMode="numeric"
                keyboardType="numeric"
                lineHeight={21}
                onBlur={() => onFieldBlur('weeklyValue')}
                onChangeText={onWeeklyValueChange}
                placeholder={i18n.t(
                  'sales_portal.edit_prospect.placeholders.weekly_value',
                )}
                value={formatLocalePrice(values.weeklyValue)}
              />
              <Typography muted paddingRight={12} size="$sm" variant="body">
                {i18n.t('sales_portal.edit_prospect.per_week')}
              </Typography>
            </XStack>
          </InputCompound.Section>
        </InputCompound.Box>
        {errors.weeklyValue ? (
          <InputCompound.Info>{errors.weeklyValue}</InputCompound.Info>
        ) : null}
      </InputCompound>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  contactRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  contactRowField: {
    flex: 1,
  },
  contactSection: {
    gap: theme.sk.spacing.spaceXsmall,
  },
  form: {
    gap: theme.sk.spacing.spaceMedium,
  },
}));
